// modules/elearning/documents/DocumentFilter.jsx
import React, { useState, useEffect, useRef } from "react";
import { 
  Search, 
  RotateCw, 
  SlidersHorizontal, 
  Plus, 
  ChevronDown, 
  Eraser, 
  ArrowUpDown, 
  ArrowDownAZ, 
  ArrowUpAZ, 
  X 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const SORTABLE_COLUMNS = [
  { value: "create_at", label: "Ngày tạo (Thời gian)" },
  { value: "file_name", label: "Tên tài liệu" },
  { value: "file_size", label: "Dung lượng file" },
  { value: "subject", label: "Môn học" },
  { value: "course", label: "Khóa học" },
  { value: "type", label: "Loại tài liệu" },
];

function getOrderHelperText(column, order) {
  if (column === "create_at") {
    return order === "asc" ? "Cũ nhất trước" : "Mới nhất trước";
  }
  if (column === "file_size") {
    return order === "asc" ? "Nhỏ nhất trước" : "Lớn nhất trước";
  }
  return order === "asc" ? "Theo chữ cái A → Z" : "Ngược chữ cái Z → A";
}

const DocumentFilter = ({ onNewClick, onFilterChange }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [subjectsRaw, setSubjectsRaw] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);

  // Popup Sắp xếp (Sort by)
  const [showSortPopup, setShowSortPopup] = useState(false);
  const [draftSortBy, setDraftSortBy] = useState("create_at");
  const [draftOrder, setDraftOrder] = useState("desc");
  const sortPopupRef = useRef(null);
  const sortPopupMobileRef = useRef(null);

  const [filters, setFilters] = useState({
    keyword: "",
    subject: "",
    course_id: "",
    type: "",
    is_active: "",
    sort_by: "create_at",
    order: "desc",
  });

  const isCustomSort = filters.sort_by !== "create_at" || filters.order !== "desc";

  // Khi mở popup sắp xếp, đồng bộ draft với filters hiện tại
  useEffect(() => {
    if (showSortPopup) {
      setDraftSortBy(filters.sort_by || "create_at");
      setDraftOrder(filters.order || "desc");
    }
  }, [showSortPopup, filters.sort_by, filters.order]);

  // Đóng popup sort khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktop = sortPopupRef.current && sortPopupRef.current.contains(e.target);
      const inMobile = sortPopupMobileRef.current && sortPopupMobileRef.current.contains(e.target);
      if (!inDesktop && !inMobile) {
        setShowSortPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Lấy options "Môn học" từ API
  useEffect(() => {
    let isMounted = true;
    setSubjectsLoading(true);
    getSubjectsLookup()
      .then((res) => {
        const items = res?.items || res;
        const list = Array.isArray(items) ? items : [];
        if (!isMounted) return;
        setSubjectsRaw(list);
        const mapped = list.map((s) => ({
          value: s.code,
          label: s.name,
        }));
        setSubjectOptions(mapped);
      })
      .catch((err) => {
        console.error("Lỗi khi lấy môn học:", err);
        if (isMounted) {
          setSubjectsRaw([]);
          setSubjectOptions([]);
        }
      })
      .finally(() => {
        if (isMounted) setSubjectsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ Khi chọn "Môn học", nạp danh sách "Khóa học" tương ứng
  useEffect(() => {
    if (!filters.subject) {
      setCourseOptions([]);
      return;
    }
    const sub = subjectsRaw.find((s) => s.code === filters.subject);
    if (!sub?.id) {
      setCourseOptions([]);
      return;
    }
    let cancelled = false;
    setCoursesLoading(true);
    getAllCoursesNoPaging({ subject_id: sub.id })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data)
          ? data
          : (data?.records ?? data?.items ?? []);
        const mapped = list.map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setCourseOptions(mapped);
      })
      .catch((err) => {
        console.error("Lỗi khi tải khóa học theo môn:", err);
        if (!cancelled) setCourseOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.subject, subjectsRaw]);

  const documentTypes = [
    { label: "Giáo trình", value: "giaotrinh" },
    { label: "Bài giảng", value: "baigiang" },
    { label: "Bài tập", value: "baitap" },
    { label: "Video bài học", value: "video" },
    { label: "Đề kiểm tra", value: "dethi" },
    { label: "Khác", value: "khac" },
  ];

  const statusOptions = [
    { value: "1", label: "Hoạt động" },
    { value: "0", label: "Tạm ngưng" },
  ];

  const isFiltering =
    Boolean(filters.keyword) ||
    Boolean(filters.subject) ||
    Boolean(filters.course_id) ||
    Boolean(filters.type) ||
    Boolean(filters.is_active) ||
    isCustomSort;

  // ✅ Responsive detector
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Debounce tìm kiếm
  useEffect(() => {
    const timer = setTimeout(() => onFilterChange?.(filters), 400);
    return () => clearTimeout(timer);
  }, [filters.keyword]);

  const updateFilter = (name, value) => {
    let updated;
    if (name === "subject") {
      // Khi thay đổi Môn học -> Tự động reset Khóa học đã chọn
      updated = { ...filters, subject: value, course_id: "" };
    } else {
      updated = { ...filters, [name]: value };
    }
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const handleApplySort = () => {
    setShowSortPopup(false);
    const updated = {
      ...filters,
      sort_by: draftSortBy,
      order: draftOrder,
    };
    setFilters(updated);
    onFilterChange?.(updated);
  };

  const handleResetSort = () => {
    setDraftSortBy("create_at");
    setDraftOrder("desc");
    setShowSortPopup(false);
    const updated = {
      ...filters,
      sort_by: "create_at",
      order: "desc",
    };
    setFilters(updated);
    onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      subject: "",
      course_id: "",
      type: "",
      is_active: "",
      sort_by: "create_at",
      order: "desc",
    };
    setDraftSortBy("create_at");
    setDraftOrder("desc");
    setFilters(cleared);
    setCourseOptions([]);
    onFilterChange?.(cleared);
  };

  // ================= RENDER COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active 
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm" 
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, disabled, placeholder }) => (
    <div className={`flex flex-col gap-1.5 w-full ${disabled ? "opacity-60" : ""}`}>
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`appearance-none w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 pr-10 rounded-xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all font-medium shadow-sm ${
                       disabled ? "bg-gray-100 cursor-not-allowed text-gray-400" : "cursor-pointer"
                     }`}
        >
          <option value="">{placeholder || `Tất cả ${label}`}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={16} className={`absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none transition-colors ${!disabled ? "group-hover:text-red-500" : ""}`} />
      </div>
    </div>
  );

  // Popup sắp xếp tái sử dụng
  const renderSortPopupContent = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <ArrowUpDown size={15} className="text-red-600" />
          <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
            Sắp xếp dữ liệu
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowSortPopup(false)}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
        >
          <X size={15} />
        </button>
      </div>

      {/* Trường 1: Tên cột */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Tên cột
        </label>
        <div className="relative">
          <select
            value={draftSortBy}
            onChange={(e) => {
              const nextCol = e.target.value;
              setDraftSortBy(nextCol);
              // Tự động gợi ý thứ tự phù hợp theo ngữ cảnh cột
              if (["file_name", "subject", "course", "type"].includes(nextCol) && draftSortBy === "create_at") {
                setDraftOrder("asc");
              } else if (nextCol === "create_at" || nextCol === "file_size") {
                setDraftOrder("desc");
              }
            }}
            className="appearance-none w-full bg-gray-50 border border-gray-200 text-gray-800 py-2.5 px-3.5 pr-10 rounded-xl text-sm font-semibold focus:outline-none focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer shadow-sm"
          >
            {SORTABLE_COLUMNS.map((col) => (
              <option key={col.value} value={col.value}>{col.label}</option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Trường 2: Loại (AZ hay ZA) */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            Loại sắp xếp
          </label>
          <span className="text-[10px] font-bold text-red-600 truncate max-w-[150px]">
            {getOrderHelperText(draftSortBy, draftOrder)}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setDraftOrder("asc")}
            className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${
              draftOrder === "asc"
                ? "bg-red-50 border-red-500 text-red-600 shadow-sm shadow-red-100"
                : "bg-gray-50 border-gray-200 text-gray-600 hover:border-red-300 hover:bg-white"
            }`}
          >
            <ArrowDownAZ size={16} />
            <span>A → Z (Tăng dần)</span>
          </button>
          <button
            type="button"
            onClick={() => setDraftOrder("desc")}
            className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${
              draftOrder === "desc"
                ? "bg-red-50 border-red-500 text-red-600 shadow-sm shadow-red-100"
                : "bg-gray-50 border-gray-200 text-gray-600 hover:border-red-300 hover:bg-white"
            }`}
          >
            <ArrowUpAZ size={16} />
            <span>Z → A (Giảm dần)</span>
          </button>
        </div>
      </div>

      {/* Nút thao tác trong Popup */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100 gap-2">
        <button
          type="button"
          onClick={handleResetSort}
          className="px-3 py-2 text-xs font-bold text-gray-500 hover:text-red-600 transition-colors"
        >
          Đặt lại mặc định
        </button>
        <button
          type="button"
          onClick={handleApplySort}
          className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl text-xs font-bold hover:shadow-md hover:shadow-red-200 transition-all active:scale-95 shadow-sm"
        >
          Áp dụng
        </button>
      </div>
    </div>
  );

  // ================= MOBILE VIEW =================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2 text-left">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm tên tài liệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>

          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />
          
          {/* Nút Sort icon-only trên Mobile */}
          <div className="relative" ref={sortPopupMobileRef}>
            <ActionButton
              icon={ArrowUpDown}
              onClick={() => setShowSortPopup((prev) => !prev)}
              active={isCustomSort}
              title="Sắp xếp dữ liệu"
            />
            
            <AnimatePresence>
              {showSortPopup && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-white rounded-2xl border border-gray-100 shadow-2xl p-4 z-50 text-left"
                >
                  {renderSortPopupContent()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc tài liệu"
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
            >
              ÁP DỤNG
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox
              label="Môn học"
              value={filters.subject}
              options={subjectOptions}
              onChange={(v) => updateFilter("subject", v)}
              placeholder={subjectsLoading ? "Đang tải môn học..." : "Tất cả Môn học"}
            />
            <SelectBox
              label="Khóa học"
              value={filters.course_id}
              options={courseOptions}
              onChange={(v) => updateFilter("course_id", v)}
              disabled={!filters.subject}
              placeholder={
                !filters.subject
                  ? "Chọn môn học trước"
                  : coursesLoading
                  ? "Đang tải khóa học..."
                  : "Tất cả Khóa học"
              }
            />
            <SelectBox label="Loại tài liệu" value={filters.type} options={documentTypes} onChange={(v) => updateFilter("type", v)} />
            <SelectBox label="Trạng thái" value={filters.is_active} options={statusOptions} onChange={(v) => updateFilter("is_active", v)} />

            {/* Sắp xếp bên trong Bottom Sheet Mobile */}
            <div className="pt-3 border-t border-gray-100 space-y-3">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest block">
                Sắp xếp dữ liệu (Sort by)
              </label>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="relative">
                  <select
                    value={filters.sort_by}
                    onChange={(e) => updateFilter("sort_by", e.target.value)}
                    className="appearance-none w-full bg-white border border-gray-200 text-gray-800 py-2.5 px-3 pr-8 rounded-xl text-xs font-semibold shadow-sm focus:outline-none focus:border-red-500"
                  >
                    {SORTABLE_COLUMNS.map((col) => (
                      <option key={col.value} value={col.value}>{col.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={filters.order}
                    onChange={(e) => updateFilter("order", e.target.value)}
                    className="appearance-none w-full bg-white border border-gray-200 text-gray-800 py-2.5 px-3 pr-8 rounded-xl text-xs font-semibold shadow-sm focus:outline-none focus:border-red-500"
                  >
                    <option value="asc">A → Z (Tăng dần)</option>
                    <option value="desc">Z → A (Giảm dần)</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ================= DESKTOP VIEW =================
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6 text-left">
      <div className="flex items-center justify-between gap-4">
        {/* Nhóm tìm kiếm & nút chức năng */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm theo tên tài liệu..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={() => clearFilters()} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />

          {/* Button Sort by: Chỉ dùng ICON, đặt bên cạnh Lọc nâng cao */}
          <div className="relative" ref={sortPopupRef}>
            <ActionButton
              icon={ArrowUpDown}
              onClick={() => setShowSortPopup((prev) => !prev)}
              active={isCustomSort}
              title="Sắp xếp dữ liệu (Sort by)"
            />

            {/* Popup Sort: 2 trường (Tên cột & Loại AZ/ZA) */}
            <AnimatePresence>
              {showSortPopup && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full mt-2 w-72 sm:w-80 bg-white rounded-2xl border border-gray-100 shadow-2xl p-4 z-50 text-left"
                >
                  {renderSortPopupContent()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {isFiltering && (
             <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-1">
                <Eraser size={14} /> Xóa lọc
             </button>
          )}
        </div>

        {/* Nút Thêm mới */}
        {hasPermission("elearning.documents", "C") && (
          <button
            onClick={onNewClick}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
          >
            <Plus size={18} />
            Thêm tài liệu
          </button>
        )}
      </div>

      {/* Vùng lọc nâng cao */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Môn học"
                value={filters.subject}
                options={subjectOptions}
                onChange={(v) => updateFilter("subject", v)}
                placeholder={subjectsLoading ? "Đang tải môn học..." : "Tất cả Môn học"}
              />
              <SelectBox
                label="Khóa học"
                value={filters.course_id}
                options={courseOptions}
                onChange={(v) => updateFilter("course_id", v)}
                disabled={!filters.subject}
                placeholder={
                  !filters.subject
                    ? "Chọn môn học trước"
                    : coursesLoading
                    ? "Đang tải khóa học..."
                    : "Tất cả Khóa học"
                }
              />
              <SelectBox label="Loại tài liệu" value={filters.type} options={documentTypes} onChange={(v) => updateFilter("type", v)} />
              <SelectBox label="Trạng thái" value={filters.is_active} options={statusOptions} onChange={(v) => updateFilter("is_active", v)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentFilter;
